import { useContext, useEffect, useState } from "react";
import { Select } from "antd";
import { Save, Search } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { AuthContext } from "../../context/AuthContext";
import { RESEARCH_SETTINGS_URL } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";

/**
 * Settings -> Company Research (trustedge research/).
 *
 * How the research agent on the lead page behaves: on/off, whether new leads
 * are researched automatically, depth, limits, and the ideal customer the fit
 * score compares against. The research itself runs in the local AI worker.
 */

function Toggle({ on, onClick, disabled, label }) {
    return (
        <button type="button" disabled={disabled} onClick={onClick} aria-pressed={on} aria-label={label}
            className={cn("relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors",
                on ? "bg-primary" : "bg-gray-300 dark:bg-gray-600", disabled && "opacity-50 cursor-not-allowed")}>
            <span className={cn("inline-block h-4 w-4 transform rounded-full bg-white transition-transform",
                on ? "translate-x-6" : "translate-x-1")} />
        </button>
    );
}

function Row({ title, help, children }) {
    return (
        <div className="flex flex-col gap-2 border-b border-gray-100 py-3 last:border-0 sm:flex-row sm:items-center sm:justify-between dark:border-gray-700/60">
            <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white">{title}</p>
                {help && <p className="max-w-xl text-xs text-gray-500 dark:text-gray-400">{help}</p>}
            </div>
            <div className="shrink-0">{children}</div>
        </div>
    );
}

const numInput = "h-8 w-24 rounded-md border border-gray-300 px-2 text-sm dark:border-gray-600 dark:bg-gray-800";

export default function CompanyResearchSettings() {
    const { fetch, update } = useApiMethods();
    const { hasPermission } = useContext(AuthContext);
    const canEdit = sessionStorage.getItem("login_role") === "super_admin"
        || hasPermission("company_research_settings", "edit");
    const [saved, setSaved] = useState(null);
    const [draft, setDraft] = useState(null);
    const [options, setOptions] = useState({ services: [], models: [] });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetch(RESEARCH_SETTINGS_URL).then(res => {
            if (res?.status === "success") { setSaved(res.data); setDraft(res.data); setOptions(res.options); }
            else ToastMsg("error", res?.message || "Could not load research settings");
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!draft) return <ContainerCard><div className="p-6 text-xs text-gray-400">Loading…</div></ContainerCard>;

    const set = (key, value) => setDraft(d => ({ ...d, [key]: value }));
    const dirty = JSON.stringify(saved) !== JSON.stringify(draft);
    const save = async () => {
        setSaving(true);
        try {
            const res = await update(RESEARCH_SETTINGS_URL, draft);
            if (res?.status === "success") { setSaved(res.data); setDraft(res.data); ToastMsg("success", "Saved"); }
            else ToastMsg("error", res?.message || "Could not save");
        } finally { setSaving(false); }
    };
    const toggleService = (key) => set("required_services", draft.required_services.includes(key)
        ? draft.required_services.filter(k => k !== key) : [...draft.required_services, key]);

    return (
        <ContainerCard>
            <div className="mx-auto max-w-4xl space-y-6 p-4 sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-start gap-2">
                        <Search className="mt-0.5 h-5 w-5 text-primary" aria-hidden="true" />
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Company Research</h2>
                            <p className="max-w-2xl text-xs text-gray-500 dark:text-gray-400">
                                The research agent on each lead page finds the organisation's website, reads its public pages and reports only what they say, with sources. It runs in the Gro Wing AI Worker on the office PC (Claude Code), not on the server.
                            </p>
                        </div>
                    </div>
                    {canEdit && (
                        <button type="button" onClick={save} disabled={!dirty || saving}
                            className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-xs font-medium text-white hover:opacity-90 disabled:opacity-40">
                            <Save className="h-3.5 w-3.5" aria-hidden="true" /> {saving ? "Saving…" : "Save"}
                        </button>
                    )}
                </div>

                <section>
                    <h3 className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Running research</h3>
                    <Row title="Company research" help="Shows the Research company button on lead pages.">
                        <Toggle label="Company research" on={draft.is_enabled} disabled={!canEdit} onClick={() => set("is_enabled", !draft.is_enabled)} />
                    </Row>
                    <Row title="Research new leads automatically"
                        help="Queues research for each new lead with an organisation name and a city or business email. CSV imports and repeat enquiries are skipped. Uses the daily limit below.">
                        <Toggle label="Research new leads" on={draft.auto_new_lead} disabled={!canEdit} onClick={() => set("auto_new_lead", !draft.auto_new_lead)} />
                    </Row>
                    <Row title="Depth" help="Quick: identity, website, basic profile and contacts (one AI call). Standard adds services, fit, sales insights and discovery questions.">
                        <div className="flex gap-1" role="radiogroup" aria-label="Depth">
                            {[["quick", "Quick"], ["standard", "Standard"]].map(([k, l]) => (
                                <button key={k} type="button" role="radio" aria-checked={draft.depth === k} disabled={!canEdit}
                                    onClick={() => set("depth", k)}
                                    className={cn("h-8 rounded-md border px-3 text-xs",
                                        draft.depth === k ? "border-primary bg-brand-50 text-primary dark:bg-brand-900/20" : "border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300")}>
                                    {l}
                                </button>
                            ))}
                        </div>
                    </Row>
                    <Row title="Search the web for the website" help="When the lead has no business email, Claude Code searches the web for candidate sites. Off: research only starts from a website someone enters.">
                        <Toggle label="Web search" on={draft.use_web_search} disabled={!canEdit} onClick={() => set("use_web_search", !draft.use_web_search)} />
                    </Row>
                    <Row title="Model" help="Claude Code model the worker uses. Sonnet is the accurate default; Haiku is faster.">
                        <select value={draft.model} disabled={!canEdit} onChange={e => set("model", e.target.value)}
                            className="h-8 rounded-md border border-gray-300 px-2 text-sm dark:border-gray-600 dark:bg-gray-800">
                            {options.models.map(m => <option key={m} value={m}>{m}</option>)}
                        </select>
                    </Row>
                    <Row title="Notify when done" help="The person who asked and the lead's owner get a notification.">
                        <Toggle label="Notify" on={draft.notify_on_complete} disabled={!canEdit} onClick={() => set("notify_on_complete", !draft.notify_on_complete)} />
                    </Row>
                </section>

                <section>
                    <h3 className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Limits</h3>
                    <Row title="Pages read per company" help="1 to 15. Contact, about, services and locations pages go first.">
                        <input type="number" min={1} max={15} className={numInput} disabled={!canEdit} value={draft.max_pages}
                            onChange={e => set("max_pages", e.target.value)} aria-label="Pages read per company" />
                    </Row>
                    <Row title="Research jobs per day" help="Across the whole organisation, automatic and manual together.">
                        <input type="number" min={1} max={500} className={numInput} disabled={!canEdit} value={draft.max_jobs_per_day}
                            onChange={e => set("max_jobs_per_day", e.target.value)} aria-label="Research jobs per day" />
                    </Row>
                    <Row title="Reuse a report for (days)" help="A report younger than this opens instead of researching again, unless someone presses Refresh. 0 always researches again.">
                        <input type="number" min={0} max={365} className={numInput} disabled={!canEdit} value={draft.cache_days}
                            onChange={e => set("cache_days", e.target.value)} aria-label="Reuse a report for days" />
                    </Row>
                </section>

                <section>
                    <h3 className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Ideal customer (fit score)</h3>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Fit = points earned ÷ points of the criteria that could be judged. What the website does not say is left out, not counted as a miss. Fit describes the profile, not buying intent.
                    </p>
                    <Row title="Target industries" help="e.g. Healthcare, Diagnostics. Healthcare also matches on hospital evidence (type, specialities, beds).">
                        <Select mode="tags" className="w-72 max-w-full" disabled={!canEdit} value={draft.target_industries}
                            onChange={v => set("target_industries", v)} tokenSeparators={[","]} open={false} aria-label="Target industries" />
                    </Row>
                    <Row title="Target locations" help="Cities or states, e.g. Tamil Nadu, Coimbatore. Empty: location is not scored.">
                        <Select mode="tags" className="w-72 max-w-full" disabled={!canEdit} value={draft.target_locations}
                            onChange={v => set("target_locations", v)} tokenSeparators={[","]} open={false} aria-label="Target locations" />
                    </Row>
                    <Row title="Minimum beds" help="Full points at or above; half points at half of it. Empty: size is not scored.">
                        <input type="number" min={0} className={numInput} disabled={!canEdit} value={draft.min_beds ?? ""}
                            onChange={e => set("min_beds", e.target.value === "" ? null : e.target.value)} aria-label="Minimum beds" />
                    </Row>
                    <Row title="Services they should have" help="Counted when their website mentions them.">
                        <div className="flex max-w-md flex-wrap gap-1.5">
                            {options.services.map(s => (
                                <button key={s.key} type="button" disabled={!canEdit} aria-pressed={draft.required_services.includes(s.key)}
                                    onClick={() => toggleService(s.key)}
                                    className={cn("rounded-full border px-2.5 py-1 text-[11px]",
                                        draft.required_services.includes(s.key) ? "border-primary bg-brand-50 text-primary dark:bg-brand-900/20" : "border-gray-300 text-gray-600 dark:border-gray-600 dark:text-gray-300")}>
                                    {s.label}
                                </button>
                            ))}
                        </div>
                    </Row>
                    <Row title="Prefer multiple branches">
                        <Toggle label="Prefer multiple branches" on={draft.prefer_multi_branch} disabled={!canEdit} onClick={() => set("prefer_multi_branch", !draft.prefer_multi_branch)} />
                    </Row>
                </section>
            </div>
        </ContainerCard>
    );
}
